import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  onSnapshot,
  DocumentData,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Case, Appointment, CaseStatus, Counselor, Mentorship } from '../types';
import {
  ActivityRecord,
  startOfDayDaysAgo,
} from '../components/Dashboard/dashboardUtils';
import { countFutureAppointments } from '../components/Calendar/calendarUtils';
import { mapFirestoreCase, isCaseVisibleToCounselor } from '../components/Cases/casesUtils';
import { mapFirestoreCounselor, dedupeCounselors } from '../components/Counselors/counselorsUtils';
import {
  logCaseAssigned,
  logCaseProposalDeclined,
} from '../utils/activityLogger';
import { filterPendingAssignments, filterActiveProposals, filterAssignmentOutcomes } from '../utils/assignmentNotifications';
import { buildApprenticeCaseSets, fetchMentorshipsForUser } from '../utils/mentorship';
import { getDocsByCaseIds } from '../utils/firestoreChunks';
import { getHistoryFloor } from '../utils/timeRange';
import { t } from '../utils/translations';

const ASSIGNMENT_ACTIVITY_TYPES = [
  'case_assigned',
  'case_proposed',
  'case_proposal_declined',
] as const;

const ASSIGNMENT_TYPE_SET = new Set<string>(ASSIGNMENT_ACTIVITY_TYPES);

/** A proposal still unanswered after six months is stale; the bell should not resurrect it. */
const ASSIGNMENT_WINDOW_DAYS = 180;
/** Ceiling on documents read per session. The window normally keeps the real count far below. */
const ASSIGNMENT_ACTIVITY_LIMIT = 500;

function mapActivityDoc(id: string, data: DocumentData): ActivityRecord | null {
  try {
    if (!ASSIGNMENT_TYPE_SET.has(data.type)) return null;
    const rawTs = data.timestamp;
    const timestamp =
      typeof rawTs?.toDate === 'function'
        ? rawTs.toDate()
        : rawTs instanceof Date
          ? rawTs
          : new Date(rawTs || Date.now());
    if (Number.isNaN(timestamp.getTime())) return null;

    return {
      id,
      type: data.type,
      title: data.title,
      description: data.description,
      timestamp,
      userId: data.userId,
      userName: data.userName,
      metadata: data.metadata,
    };
  } catch (err) {
    console.error('Skipping malformed assignment activity:', id, err);
    return null;
  }
}

function activityTargetsCounselor(
  activity: ActivityRecord,
  userId: string,
  counselorRecordId: string | null
): boolean {
  const assignedTo = activity.metadata?.assignedToUserId;
  if (assignedTo === userId) return true;
  return Boolean(counselorRecordId) && assignedTo === counselorRecordId;
}

function snapshotToAssignmentActivities(snapshot: QuerySnapshot): ActivityRecord[] {
  const items: ActivityRecord[] = [];
  snapshot.forEach((actDoc) => {
    const mapped = mapActivityDoc(actDoc.id, actDoc.data());
    if (mapped) items.push(mapped);
  });
  return items;
}

export interface DashboardMetricsComputed {
  totalCases: number;
  activeCases: number;
  completedCases: number;
  pendingCases: number;
  totalAppointments: number;
  futureAppointmentsCount: number;
  casesByStatus: Record<CaseStatus, number>;
}

export function useDashboardData() {
  const { currentUser } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [counselors, setCounselors] = useState<Counselor[]>([]);
  const [sessionReportCounts, setSessionReportCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counselorRecordId, setCounselorRecordId] = useState<string | null>(null);
  const [mentorships, setMentorships] = useState<Mentorship[]>([]);
  const [newAssignmentModal, setNewAssignmentModal] = useState<ActivityRecord | null>(null);
  const [dismissedAssignments, setDismissedAssignments] = useState<Set<string>>(new Set());
  const [dismissedAssignmentsLoaded, setDismissedAssignmentsLoaded] = useState(false);
  const [rawPendingAssignments, setRawPendingAssignments] = useState<ActivityRecord[]>([]);
  const [assignmentOutcomes, setAssignmentOutcomes] = useState<ActivityRecord[]>([]);
  const [allAssignmentActivities, setAllAssignmentActivities] = useState<ActivityRecord[]>([]);
  const [missingAssignmentCaseIds, setMissingAssignmentCaseIds] = useState<Set<string>>(
    () => new Set()
  );
  const casesRef = useRef(cases);
  casesRef.current = cases;
  const allAssignmentActivitiesRef = useRef(allAssignmentActivities);
  allAssignmentActivitiesRef.current = allAssignmentActivities;
  const dismissedAssignmentsRef = useRef(dismissedAssignments);
  dismissedAssignmentsRef.current = dismissedAssignments;

  const upsertCase = useCallback((caseItem: Case) => {
    setCases((prev) => {
      const index = prev.findIndex((c) => c.id === caseItem.id);
      if (index === -1) {
        return [caseItem, ...prev];
      }
      const next = [...prev];
      next[index] = caseItem;
      return next;
    });
  }, []);

  const removeCase = useCallback((caseId: string) => {
    setCases((prev) => prev.filter((c) => c.id !== caseId));
    setSessionReportCounts((prev) => {
      if (!(caseId in prev)) return prev;
      const next = { ...prev };
      delete next[caseId];
      return next;
    });
  }, []);

  const loadDismissedAssignments = async (userId: string) => {
    try {
      const dismissedRef = doc(db, 'dismissedAssignments', userId);
      const dismissedSnap = await getDoc(dismissedRef);

      if (dismissedSnap.exists()) {
        const data = dismissedSnap.data();
        setDismissedAssignments(new Set(data.activityIds || []));
      } else {
        setDismissedAssignments(new Set());
      }
    } catch (err) {
      console.error('Error loading dismissed assignments:', err);
      setDismissedAssignments(new Set());
    } finally {
      setDismissedAssignmentsLoaded(true);
    }
  };

  /**
   * One merged write, whatever state the document is in. The previous read-then-branch cost a
   * read per dismissal and raced with itself: concurrent callers could all find the document
   * missing and each overwrite the others with a fresh `setDoc`. `createdAt` is gone because a
   * merged write would rewrite it every time, making it a lie; nothing reads it.
   */
  const saveDismissedAssignments = async (userId: string, activityIds: string[]) => {
    if (activityIds.length === 0) return;
    try {
      await setDoc(
        doc(db, 'dismissedAssignments', userId),
        { userId, activityIds: arrayUnion(...activityIds), updatedAt: new Date() },
        { merge: true }
      );
    } catch (err) {
      console.error('Error saving dismissed assignments:', err);
    }
  };

  const clearAssignmentsForMissingCase = useCallback(
    async (caseId: string, activities: ActivityRecord[]) => {
      if (!currentUser?.id || !caseId) return;

      const toClear = activities.filter((activity) => {
        if (String(activity.metadata?.caseId || '') !== caseId) return false;

        if (
          (activity.type === 'case_proposed' || activity.type === 'case_assigned') &&
          activityTargetsCounselor(activity, currentUser.id, counselorRecordId)
        ) {
          return true;
        }

        // Accept/refuse outcome addressed to this leader. With the case gone there is
        // nothing left to reassign, so the bell item would never resolve on its own.
        return activity.metadata?.notifyUserId === currentUser.id;
      });

      if (toClear.length === 0) {
        setNewAssignmentModal((current) =>
          current && String(current.metadata?.caseId || '') === caseId ? null : current
        );
        return;
      }

      setDismissedAssignments((prev) => {
        const next = new Set(prev);
        toClear.forEach((activity) => next.add(activity.id));
        return next;
      });
      setRawPendingAssignments((prev) =>
        prev.filter((activity) => String(activity.metadata?.caseId || '') !== caseId)
      );
      const clearedIds = new Set(toClear.map((activity) => activity.id));
      setAssignmentOutcomes((prev) =>
        prev.filter((activity) => !clearedIds.has(activity.id))
      );
      setNewAssignmentModal((current) =>
        current && String(current.metadata?.caseId || '') === caseId ? null : current
      );
      setMissingAssignmentCaseIds((prev) => {
        const next = new Set(prev);
        next.add(caseId);
        return next;
      });

      await saveDismissedAssignments(
        currentUser.id,
        toClear.map((activity) => activity.id)
      );
    },
    [currentUser?.id, counselorRecordId]
  );

  /**
   * Persist-dismiss accept/refuse bell items for a case that has been dealt with
   * (reassigned). Without this the item is only hidden while the case stays
   * assigned, so a second refusal would resurface the original one too.
   */
  const resolveAssignmentOutcomesForCase = useCallback(
    async (caseId: string) => {
      if (!currentUser?.id || !caseId) return;

      const toResolve = allAssignmentActivitiesRef.current.filter((activity) => {
        if (String(activity.metadata?.caseId || '') !== caseId) return false;
        if (activity.metadata?.notifyUserId !== currentUser.id) return false;
        if (dismissedAssignmentsRef.current.has(activity.id)) return false;
        return (
          activity.type === 'case_proposal_declined' ||
          (activity.type === 'case_assigned' &&
            activity.metadata?.assignmentSource === 'proposal_accept')
        );
      });

      if (toResolve.length === 0) return;

      const resolvedIds = new Set(toResolve.map((activity) => activity.id));
      setDismissedAssignments((prev) => new Set([...Array.from(prev), ...Array.from(resolvedIds)]));
      setAssignmentOutcomes((prev) =>
        prev.filter((activity) => !resolvedIds.has(activity.id))
      );

      await saveDismissedAssignments(currentUser.id, Array.from(resolvedIds));
    },
    [currentUser?.id]
  );

  /**
   * The three fields `loadData` actually reads. Depending on these instead of the whole
   * `currentUser` object is what keeps the reload from firing on every unrelated write to the
   * user document: `persistUser` hands out a fresh object each time, so a new FCM token, an
   * avatar change or the tutorial flag used to look identical to a change of user and set off
   * the full cases/appointments/counselors/reports fetch again.
   */
  const userId = currentUser?.id;
  const userRole = currentUser?.role;
  const userEmail = currentUser?.email;

  const loadData = useCallback(async () => {
    if (!userId || !userEmail) return;

    const findCounselorRecordId = async () => {
      if (userRole !== 'leader' && userRole !== 'admin' && userRole !== 'counselor') {
        return null;
      }

      try {
        const counselorsRef = collection(db, 'counselors');
        const linkedQuery = query(counselorsRef, where('linkedUserId', '==', userId));
        const linkedSnapshot = await getDocs(linkedQuery);

        if (!linkedSnapshot.empty) {
          return linkedSnapshot.docs[0].id;
        }

        const emailQuery = query(counselorsRef, where('email', '==', userEmail));
        const emailSnapshot = await getDocs(emailQuery);
        if (!emailSnapshot.empty) {
          return emailSnapshot.docs[0].id;
        }
      } catch (err) {
        console.error('Error finding counselor record:', err);
      }
      return null;
    };

    try {
      setLoading(true);
      setError(null);

      const counselorId = await findCounselorRecordId();
      setCounselorRecordId(counselorId);

      // Mentor cases granted to this user for shadowing. Loaded before the
      // cases scan because the visibility filter below needs the ids.
      let userMentorships: Mentorship[] = [];
      try {
        userMentorships = await fetchMentorshipsForUser(userId);
      } catch (err) {
        console.error('Error loading mentorships:', err);
      }
      setMentorships(userMentorships);
      const apprenticeCases = buildApprenticeCaseSets(userId, userMentorships);

      const casesRef = collection(db, 'cases');
      const casesQuery = query(casesRef, orderBy('createdAt', 'desc'));
      const casesSnapshot = await getDocs(casesQuery);

      const casesData: Case[] = [];
      casesSnapshot.forEach((caseDoc) => {
        const caseItem = mapFirestoreCase(caseDoc.id, caseDoc.data());
        if (
          isCaseVisibleToCounselor(
            caseItem,
            counselorId,
            userId,
            apprenticeCases.all
          )
        ) {
          casesData.push(caseItem);
        }
      });

      const appointmentsRef = collection(db, 'appointments');
      // Same floor as the calendar, which writes into this same appointment cache. Inequality
      // and orderBy on one field, so the automatic single-field index serves it.
      const appointmentsQuery = query(
        appointmentsRef,
        where('date', '>=', getHistoryFloor()),
        orderBy('date', 'asc')
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);

      const appointmentsData: Appointment[] = [];
      appointmentsSnapshot.forEach((aptDoc) => {
        const data = aptDoc.data();
        let isUserAppointment = false;

        if (counselorId) {
          isUserAppointment = data.counselorId === counselorId || data.counselorId === userId;
        } else {
          isUserAppointment = data.counselorId === userId || data.clientId === userId;
        }

        // Shadowing only works if the apprentice knows when the sessions are.
        if (!isUserAppointment && data.caseId) {
          isUserAppointment = apprenticeCases.all.has(data.caseId);
        }

        if (isUserAppointment) {
          appointmentsData.push({
            id: aptDoc.id,
            title: data.title,
            description: data.description,
            sessionNumber:
              typeof data.sessionNumber === 'number'
                ? Math.max(0, Math.floor(data.sessionNumber))
                : null,
            date: data.date.toDate(),
            startTime: data.startTime,
            endTime: data.endTime,
            counselorId: data.counselorId,
            counselorName: data.counselorName,
            caseId: data.caseId,
            caseTitle: data.caseTitle,
            room: data.room,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
          });
        }
      });

      let counselorsData: Counselor[] = [];
      const counselorsSnapshot = await getDocs(collection(db, 'counselors'));
      counselorsSnapshot.forEach((docSnap) => {
        counselorsData.push(mapFirestoreCounselor(docSnap.id, docSnap.data()));
      });
      counselorsData = dedupeCounselors(counselorsData).sort((a, b) =>
        a.fullName.localeCompare(b.fullName, 'ro')
      );

      // Scoped to the cases this user can see, which `casesData` has already narrowed. Only
      // the count per case is ever used, so nothing beyond `caseId` is read off these docs.
      const reportDocs = await getDocsByCaseIds(
        'sessionReports',
        casesData.map((caseItem) => caseItem.id)
      );
      const reportCounts: Record<string, number> = {};
      reportDocs.forEach((reportDoc) => {
        const caseId = reportDoc.data().caseId as string;
        if (caseId) {
          reportCounts[caseId] = (reportCounts[caseId] ?? 0) + 1;
        }
      });

      setCases(casesData);
      setAppointments(appointmentsData);
      setCounselors(counselorsData);
      setSessionReportCounts(reportCounts);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, userRole, userEmail]);

  useEffect(() => {
    loadData();
    if (currentUser?.id) {
      loadDismissedAssignments(currentUser.id);
    }
  }, [currentUser?.id, loadData]);

  useEffect(() => {
    if (
      !currentUser ||
      (currentUser.role !== 'counselor' &&
        currentUser.role !== 'admin' &&
        currentUser.role !== 'leader')
    ) {
      setAllAssignmentActivities([]);
      return;
    }

    if (!dismissedAssignmentsLoaded) return;

    const userId = currentUser.id;
    const activitiesRef = collection(db, 'activities');
/**
 * The query is bounded by time rather than by type. An inequality on `timestamp`, ordered and
 * limited on that same field, is served by Firestore's automatic single-field index — adding
 * `type` back into it would need a composite index, which is what previously broke this
 * listener. The type filter therefore moves into the mapper below, where it costs nothing.
 */
    const windowStart = startOfDayDaysAgo(ASSIGNMENT_WINDOW_DAYS);
    const activitiesQuery = query(
      activitiesRef,
      where('timestamp', '>=', windowStart),
      orderBy('timestamp', 'desc'),
      limit(ASSIGNMENT_ACTIVITY_LIMIT)
    );

    const warmCasesForActivities = async (
      activities: ActivityRecord[],
      allActivities: ActivityRecord[]
    ) => {
      const caseIdsToFetch = new Set<string>();
      const outcomeCaseIds = new Set<string>();

      for (const activity of activities) {
        const caseId = String(activity.metadata?.caseId || '');
        if (!caseId) continue;

        // Assigner: proposal accepted/declined → drop from personal Cases if no longer theirs
        if (
          activity.metadata?.notifyUserId === userId &&
          !dismissedAssignmentsRef.current.has(activity.id)
        ) {
          const isDecline = activity.type === 'case_proposal_declined';
          const isAccept =
            activity.type === 'case_assigned' &&
            activity.metadata?.assignmentSource === 'proposal_accept';
          if (isDecline || isAccept) {
            outcomeCaseIds.add(caseId);
          }
        }

        if (activity.type !== 'case_proposed' && activity.type !== 'case_assigned') {
          continue;
        }
        if (!activityTargetsCounselor(activity, userId, counselorRecordId)) {
          continue;
        }

        const existing = casesRef.current.find((c) => c.id === caseId);
        const needsFetch =
          !existing ||
          (activity.type === 'case_proposed' &&
            (existing.assignmentStatus !== 'pending' ||
              (Boolean(counselorRecordId) &&
                existing.proposedCounselorId !== counselorRecordId)));

        if (needsFetch) {
          caseIdsToFetch.add(caseId);
        }
      }

      const ids = Array.from(new Set([...caseIdsToFetch, ...outcomeCaseIds]));
      await Promise.all(
        ids.map(async (caseId) => {
          try {
            const caseSnap = await getDoc(doc(db, 'cases', caseId));
            if (!caseSnap.exists()) {
              removeCase(caseId);
              await clearAssignmentsForMissingCase(caseId, allActivities);
              return;
            }
            // Always upsert cases tied to live assignment activities so the bell/modal
            // are not blocked waiting for personal-cases visibility heuristics.
            upsertCase(mapFirestoreCase(caseSnap.id, caseSnap.data()));
            setMissingAssignmentCaseIds((prev) => {
              if (!prev.has(caseId)) return prev;
              const next = new Set(prev);
              next.delete(caseId);
              return next;
            });
          } catch (err) {
            console.error('Error warming case for assignment activity:', err);
          }
        })
      );
    };

    const applyActivities = (items: ActivityRecord[], changed?: ActivityRecord[]) => {
      const sorted = [...items].sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
      );
      setAllAssignmentActivities(sorted);
      void warmCasesForActivities(changed ?? sorted, sorted);
    };

    const unsubscribe = onSnapshot(
      activitiesQuery,
      (snapshot) => {
        const items = snapshotToAssignmentActivities(snapshot);
        const changed = snapshot
          .docChanges()
          .filter((change) => change.type === 'added' || change.type === 'modified')
          .map((change) => mapActivityDoc(change.doc.id, change.doc.data()))
          .filter((item): item is ActivityRecord => item !== null);
        applyActivities(items, changed);
      },
      (listenerError) => {
        console.error('Error listening to case assignments:', listenerError);
      }
    );

    return unsubscribe;
  }, [
    currentUser,
    counselorRecordId,
    dismissedAssignmentsLoaded,
    upsertCase,
    removeCase,
    clearAssignmentsForMissingCase,
  ]);

  // Live pending proposals for this counselor — keeps case cache warm so the modal can open
  // as soon as the matching activity arrives (leader writes the case before the activity).
  useEffect(() => {
    if (!currentUser || !counselorRecordId) return;
    if (
      currentUser.role !== 'counselor' &&
      currentUser.role !== 'admin' &&
      currentUser.role !== 'leader'
    ) {
      return;
    }

    const pendingProposalsQuery = query(
      collection(db, 'cases'),
      where('proposedCounselorId', '==', counselorRecordId)
    );

    const unsubscribe = onSnapshot(
      pendingProposalsQuery,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            const removedId = change.doc.id;
            setCases((prev) => {
              const existing = prev.find((c) => c.id === removedId);
              if (!existing || existing.assignmentStatus !== 'pending') return prev;
              return prev.map((c) =>
                c.id === removedId
                  ? {
                      ...c,
                      proposedCounselorId: null,
                      proposedCounselorName: null,
                      assignmentStatus: 'none' as const,
                    }
                  : c
              );
            });
            return;
          }
          if (change.type === 'added' || change.type === 'modified') {
            // Force into cache for proposal UX.
            upsertCase(mapFirestoreCase(change.doc.id, change.doc.data()));
          }
        });
      },
      (err) => {
        console.error('Error listening to pending proposal cases:', err);
      }
    );

    return unsubscribe;
  }, [currentUser, counselorRecordId, upsertCase]);

  // Re-filter in memory when activities or dismissals change (no re-query).
  useEffect(() => {
    if (
      !currentUser ||
      (currentUser.role !== 'counselor' &&
        currentUser.role !== 'admin' &&
        currentUser.role !== 'leader')
    ) {
      setRawPendingAssignments([]);
      setAssignmentOutcomes([]);
      return;
    }

    if (!dismissedAssignmentsLoaded) return;

    const caseAssignedActivities = filterPendingAssignments(
      allAssignmentActivities,
      currentUser.id,
      counselorRecordId,
      dismissedAssignments
    );

    setRawPendingAssignments(caseAssignedActivities);
    setAssignmentOutcomes(
      filterAssignmentOutcomes(
        allAssignmentActivities,
        currentUser.id,
        dismissedAssignments
      )
    );
    setNewAssignmentModal((current) => {
      if (!current) return null;
      if (current.type === 'case_proposed') return current;
      const stillPending = caseAssignedActivities.some((a) => a.id === current.id);
      return stillPending ? current : null;
    });
  }, [
    currentUser,
    counselorRecordId,
    dismissedAssignments,
    dismissedAssignmentsLoaded,
    allAssignmentActivities,
  ]);

  const pendingAssignments = useMemo(() => {
    const proposals = filterActiveProposals(
      rawPendingAssignments,
      cases,
      counselorRecordId
    );
    const assigned = rawPendingAssignments.filter((a) => a.type === 'case_assigned');
    return [...proposals, ...assigned];
  }, [rawPendingAssignments, cases, counselorRecordId]);

  const pendingAssignmentCount = pendingAssignments.length;

  // Propunere / alocare: auto-open modal as soon as live activities arrive.
  useEffect(() => {
    if (loading || !dismissedAssignmentsLoaded) return;

    const proposals = pendingAssignments.filter((a) => a.type === 'case_proposed');
    const firstProposal = proposals[0] ?? null;
    const assigned = pendingAssignments.filter((a) => a.type === 'case_assigned');
    const firstAssigned = assigned[0] ?? null;

    setNewAssignmentModal((current) => {
      if (current?.type === 'case_assigned') {
        const stillPending = assigned.some((a) => a.id === current.id);
        if (stillPending) return current;
        return firstProposal ?? firstAssigned;
      }

      if (firstProposal) {
        if (
          current?.type === 'case_proposed' &&
          proposals.some((p) => p.id === current.id)
        ) {
          return current;
        }
        return firstProposal;
      }

      if (current?.type === 'case_proposed') {
        return firstAssigned;
      }

      if (firstAssigned) {
        if (current?.id === firstAssigned.id) return current;
        if (!current) return firstAssigned;
        return firstAssigned;
      }

      return current;
    });
  }, [loading, dismissedAssignmentsLoaded, pendingAssignments]);

  const dismissAssignment = useCallback(
    async (activityId: string) => {
      if (!currentUser?.id) return;
      setDismissedAssignments((prev) => new Set(Array.from(prev).concat(activityId)));
      await saveDismissedAssignments(currentUser.id, [activityId]);
      setNewAssignmentModal(null);
      setRawPendingAssignments((prev) => prev.filter((a) => a.id !== activityId));
      setAssignmentOutcomes((prev) => prev.filter((a) => a.id !== activityId));
    },
    [currentUser?.id]
  );

  const [assignmentActionLoading, setAssignmentActionLoading] = useState<'accept' | 'refuse' | null>(null);
  const [assignmentActionError, setAssignmentActionError] = useState<string | null>(null);

  const openAssignment = useCallback((activity: ActivityRecord) => {
    setAssignmentActionError(null);
    setNewAssignmentModal(activity);
  }, []);

  const acceptProposal = useCallback(async () => {
    if (!currentUser || !newAssignmentModal?.metadata?.caseId) return;
    const caseId = String(newAssignmentModal.metadata.caseId);
    const activityId = newAssignmentModal.id;

    try {
      setAssignmentActionLoading('accept');
      setAssignmentActionError(null);

      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      if (!caseSnap.exists()) {
        await clearAssignmentsForMissingCase(caseId, allAssignmentActivitiesRef.current);
        return;
      }
      const data = caseSnap.data();
      const proposedId = data.proposedCounselorId;
      const proposedName = data.proposedCounselorName || currentUser.fullName;
      const notifyUserId =
        (data.proposedByUserId as string | undefined) ||
        newAssignmentModal.userId ||
        null;

      await updateDoc(caseRef, {
        assignedCounselorId: proposedId,
        assignedCounselorName: proposedName,
        proposedCounselorId: null,
        proposedCounselorName: null,
        proposedByUserId: null,
        proposedByUserName: null,
        assignmentStatus: 'accepted',
        status: 'active',
        updatedAt: new Date(),
      });

      await logCaseAssigned(
        caseId,
        data.title || String(newAssignmentModal.metadata.caseTitle || ''),
        currentUser.id,
        currentUser.fullName || currentUser.email || 'Unknown',
        currentUser.id,
        currentUser.fullName || currentUser.email || 'Unknown',
        'proposal_accept',
        notifyUserId
      );

      await dismissAssignment(activityId);
      await loadData();
    } catch (err) {
      console.error('Accept proposal error:', err);
      setAssignmentActionError(t.assignments.acceptError);
    } finally {
      setAssignmentActionLoading(null);
    }
  }, [
    currentUser,
    newAssignmentModal,
    dismissAssignment,
    loadData,
    clearAssignmentsForMissingCase,
  ]);

  const refuseProposal = useCallback(async () => {
    if (!currentUser || !newAssignmentModal?.metadata?.caseId) return;
    const caseId = String(newAssignmentModal.metadata.caseId);
    const activityId = newAssignmentModal.id;

    try {
      setAssignmentActionLoading('refuse');
      setAssignmentActionError(null);

      const caseRef = doc(db, 'cases', caseId);
      const caseSnap = await getDoc(caseRef);
      if (!caseSnap.exists()) {
        await clearAssignmentsForMissingCase(caseId, allAssignmentActivitiesRef.current);
        return;
      }
      const data = caseSnap.data();
      const notifyUserId =
        (data.proposedByUserId as string | undefined) ||
        newAssignmentModal.userId ||
        null;

      await updateDoc(caseRef, {
        proposedCounselorId: null,
        proposedCounselorName: null,
        proposedByUserId: null,
        proposedByUserName: null,
        assignmentStatus: 'none',
        updatedAt: new Date(),
      });

      await logCaseProposalDeclined(
        caseId,
        data.title || String(newAssignmentModal.metadata.caseTitle || ''),
        currentUser.id,
        currentUser.fullName || currentUser.email || 'Unknown',
        notifyUserId
      );

      await dismissAssignment(activityId);
      await loadData();
    } catch (err) {
      console.error('Refuse proposal error:', err);
      setAssignmentActionError(t.assignments.refuseError);
    } finally {
      setAssignmentActionLoading(null);
    }
  }, [currentUser, newAssignmentModal, dismissAssignment, loadData, clearAssignmentsForMissingCase]);

  // Sweep: drop assignment notifications whose cases were deleted.
  useEffect(() => {
    if (!currentUser || !dismissedAssignmentsLoaded) return;

    const caseIds = Array.from(
      new Set(
        pendingAssignments
          .map((activity) => String(activity.metadata?.caseId || ''))
          .filter(Boolean)
      )
    );

    caseIds.forEach((caseId) => {
      if (cases.some((caseItem) => caseItem.id === caseId)) return;
      void getDoc(doc(db, 'cases', caseId)).then((snap) => {
        if (!snap.exists()) {
          void clearAssignmentsForMissingCase(caseId, allAssignmentActivitiesRef.current);
        }
      });
    });
  }, [
    currentUser,
    dismissedAssignmentsLoaded,
    pendingAssignments,
    cases,
    clearAssignmentsForMissingCase,
  ]);

  /** Drop every bell item tied to a case that no longer exists (deleted by an admin). */
  const clearAssignmentNotificationsForCase = useCallback(
    async (caseId: string) => {
      await clearAssignmentsForMissingCase(caseId, allAssignmentActivitiesRef.current);
    },
    [clearAssignmentsForMissingCase]
  );

  const clearStaleAssignmentModal = useCallback(async () => {
    const caseId = newAssignmentModal?.metadata?.caseId
      ? String(newAssignmentModal.metadata.caseId)
      : '';
    if (!caseId) {
      setNewAssignmentModal(null);
      return;
    }
    await clearAssignmentNotificationsForCase(caseId);
  }, [newAssignmentModal, clearAssignmentNotificationsForCase]);

  const isAssignmentCaseMissing = Boolean(
    newAssignmentModal?.metadata?.caseId &&
      missingAssignmentCaseIds.has(String(newAssignmentModal.metadata.caseId))
  );

  const apprenticeCaseSets = useMemo(
    () => buildApprenticeCaseSets(currentUser?.id || '', mentorships),
    [currentUser?.id, mentorships]
  );

  /**
   * Cases that are actually this user's work. A shadowed mentor case is visible
   * but must not land in workload counters — unless it also happens to be
   * assigned or proposed to the user, which the plain predicate still catches.
   */
  const ownCases = useMemo(
    () =>
      cases.filter(
        (caseItem) =>
          !apprenticeCaseSets.all.has(caseItem.id) ||
          isCaseVisibleToCounselor(caseItem, counselorRecordId, currentUser?.id || '')
      ),
    [cases, apprenticeCaseSets, counselorRecordId, currentUser?.id]
  );

  const metrics: DashboardMetricsComputed = useMemo(() => {
    const casesByStatus = ownCases.reduce(
      (acc, caseItem) => {
        acc[caseItem.status] = (acc[caseItem.status] || 0) + 1;
        return acc;
      },
      {} as Record<CaseStatus, number>
    );

    return {
      totalCases: ownCases.length,
      activeCases: ownCases.filter((c) => c.status === 'active').length,
      completedCases: ownCases.filter((c) => c.status === 'finished').length,
      pendingCases: ownCases.filter((c) => c.status === 'waiting').length,
      totalAppointments: appointments.length,
      futureAppointmentsCount: countFutureAppointments(appointments),
      casesByStatus,
    };
  }, [ownCases, appointments]);

  const upcomingAppointments = useMemo(
    () =>
      appointments
        .filter((apt) => {
          const appointmentDateTime = new Date(apt.date);
          if (apt.startTime) {
            const [hours, minutes] = apt.startTime.split(':').map(Number);
            appointmentDateTime.setHours(hours, minutes, 0, 0);
          }
          return appointmentDateTime > new Date();
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [appointments]
  );

  const activeCasesList = useMemo(
    () =>
      cases
        .filter((c) => c.status === 'active')
        .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()),
    [cases]
  );

  const upsertAppointment = useCallback((appointment: Appointment) => {
    setAppointments((prev) => {
      const index = prev.findIndex((a) => a.id === appointment.id);
      if (index === -1) {
        return [...prev, appointment].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      }
      const next = [...prev];
      next[index] = appointment;
      return next;
    });
  }, []);

  const removeAppointment = useCallback((appointmentId: string) => {
    setAppointments((prev) => prev.filter((a) => a.id !== appointmentId));
  }, []);

  const replaceAppointments = useCallback((next: Appointment[]) => {
    setAppointments(next);
  }, []);

  const incrementSessionReportCount = useCallback((caseId: string) => {
    setSessionReportCounts((prev) => ({
      ...prev,
      [caseId]: (prev[caseId] ?? 0) + 1,
    }));
  }, []);

  const replaceCounselors = useCallback((next: Counselor[]) => {
    setCounselors(next);
  }, []);

  return {
    cases,
    /** `cases` minus shadowed mentor cases — use for anything workload-shaped. */
    ownCases,
    mentorships,
    /** Mentor cases the user may open, active or completed pairing alike. */
    apprenticeCaseIds: apprenticeCaseSets.all,
    /** Subset the user may still write their own notes on. */
    apprenticeWritableCaseIds: apprenticeCaseSets.writable,
    appointments,
    counselors,
    sessionReportCounts,
    metrics,
    upcomingAppointments,
    activeCasesList,
    loading,
    /**
     * Whether `pendingAssignments`/`assignmentOutcomes` reflect the user's real dismissals yet.
     * Both stay `[]` until this is true, so a consumer turning them into a count (the
     * notification bell) should wait for it too — otherwise it settles from `0`, which reads
     * fine on its own but is still one more transient state than the real number arriving once.
     */
    dismissedAssignmentsLoaded,
    error,
    counselorRecordId,
    newAssignmentModal,
    setNewAssignmentModal,
    openAssignment,
    dismissAssignment,
    acceptProposal,
    refuseProposal,
    clearStaleAssignmentModal,
    clearAssignmentNotificationsForCase,
    resolveAssignmentOutcomesForCase,
    isAssignmentCaseMissing,
    assignmentActionLoading,
    assignmentActionError,
    pendingAssignments,
    pendingAssignmentCount,
    assignmentOutcomes,
    refetch: loadData,
    upsertCase,
    removeCase,
    upsertAppointment,
    removeAppointment,
    replaceAppointments,
    incrementSessionReportCount,
    replaceCounselors,
  };
}
